"use client";

/**
 * Accordion View
 * Renders schedule items grouped by date as white panels that open and close, one per day. The
 * header names the day and how many items it holds; open panels list the items as rows.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { FetchButton } from "@/fetch-ui/FetchButton";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import type { ViewerRegistrationKeys } from "../../../../_logic/utils/schedule-registration-match";
import { formatScheduleDayHeading } from "../../../../_logic/utils/schedule-display-format";
import { useEventToday } from "../../../../_hooks/useEventToday";
import { useScheduleLiveMarkers } from "../../../../_hooks/useScheduleLiveMarkers";
import { ScheduleRow, type ScheduleRowConfig } from "./ScheduleRow";

const NO_ITEMS: ScheduleItemRow[] = [];

interface AccordionViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for detail modal) */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/** Renders schedule items in collapsible panels grouped by date */
export function AccordionView({
  dateGroups,
  labels,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
}: AccordionViewProps) {
  // Hooks
  const todayStr = useEventToday(timeZone);

  // States
  const [expandedDates, setExpandedDates] = useState<Set<string | null>>(() => new Set(dateGroups.map((g) => g.date)));

  // Derived values
  const todayItems = useMemo(
    () => dateGroups.find((g) => g.date !== null && g.date === todayStr)?.items ?? NO_ITEMS,
    [dateGroups, todayStr]
  );
  const { liveItemIds, upcomingMinutesById } = useScheduleLiveMarkers(todayItems, timeZone);
  const rowConfig = useMemo<ScheduleRowConfig>(
    () => ({ labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations, surface: "card" }),
    [labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations]
  );
  const allExpanded = useMemo(() => dateGroups.every((g) => expandedDates.has(g.date)), [dateGroups, expandedDates]);

  // Handlers
  const toggleDate = useCallback((date: string | null) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (allExpanded) {
      setExpandedDates(new Set());
    } else {
      setExpandedDates(new Set(dateGroups.map((g) => g.date)));
    }
  }, [allExpanded, dateGroups]);

  // Effects: expand newly added date groups
  useEffect(() => {
    setExpandedDates((prev) => {
      const currentDates = new Set(dateGroups.map((g) => g.date));
      const newDates = [...currentDates].filter((d) => !prev.has(d));
      if (newDates.length === 0) return prev;
      return new Set([...prev, ...newDates]);
    });
  }, [dateGroups]);

  // Render
  return (
    <div className="flex flex-col gap-3">
      {dateGroups.length > 1 && (
        <div className="flex justify-end">
          <FetchButton
            variant="ghost"
            color="neutral"
            size="xs"
            className="touch-manipulation"
            leftIcon={
              allExpanded ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />
            }
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </FetchButton>
        </div>
      )}
      {dateGroups.map((group) => {
        const isExpanded = expandedDates.has(group.date);
        const itemCount = group.items.length;
        return (
          <div key={group.date ?? "no-date"} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => toggleDate(group.date)}
              className="flex min-h-14 w-full touch-manipulation items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50 sm:px-6"
              aria-expanded={isExpanded}
            >
              <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span className="text-lg font-bold tracking-tight text-gray-900">
                  {group.date ? formatScheduleDayHeading(group.date) : "Anytime"}
                </span>
                <span className="text-sm font-medium text-gray-500">
                  {itemCount} {itemCount === 1 ? "item" : "items"}
                </span>
              </span>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            {isExpanded && (
              <ul className="flex list-none flex-col divide-y divide-gray-100 border-t border-gray-100 px-5 sm:px-6">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <ScheduleRow
                      config={rowConfig}
                      item={item}
                      isLive={liveItemIds.has(item.id)}
                      startsInMinutes={upcomingMinutesById.get(item.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
